import { createListener, TUndoListener } from './create-listener';

export interface IClickableElementConfig {
  text: string;
  href?: string;
  className?: string;
  eventId?: string;
  callback: () => void;
}

export interface IClickableElement {
  html: string;
  removeClickEventListener: TUndoListener;
}

/**
 * Generates an HTML element with a `onclick` callback that you can inject into a transloco translation variable. The element will be a
 * `<span>` by default but you can provide an `href` to make it a `<a>`. You will have to pass the final text in Angular's DomSanitizer
 * `bypassSecurityTrustHtml()` function to make it work. Under the hood, the element blocks the click event and triggers a custom event
 * instead. This event is binded with the `callback` provided in the config. You will thus have to remove the event listener by calling
 * the `removeClickEventListener` returned by this helper.
 *
 * Complete example:
 * ```
 * <!-- Template -->
 * <div [innerHTML]="customText"></div>
 * ```
 *
 * ```
 * // Controller
 * class MyAwesomeComponent implements OnDestroy {
 *   private myClickableElement: IClickableElement;
 *
 *   constructor (
 *     protected domSanitizer: DomSanitizer,
 *     protected router: Router,
 *   ) {
 *     this.myClickableElement = clickableElement({
 *       text: 'clickable link',
 *       href: `https://fully.qualified.url/for/opening/in/new/tab`,
 *       callback: () => {
 *         this.router.navigate(['my', 'route']);
 *       },
 *     });
 *   }
 *
 *   get customText(): SafeHtml {
 *     const text = this.translocoService.translate(
 *       'This is my custom text that contain a $clickableLink$',
 *       { clickableLink: this.myClickableElement.html }
 *     );
 *     return this.domSanitizer.bypassSecurityTrustHtml(text);
 *   }
 *
 *   ngOnDestroy(): void {
 *     this.myClickableElement.removeClickEventListener();
 *   }
 * }
 * ```
 */
export function clickableElement(config: IClickableElementConfig): IClickableElement {
  // handle defaults
  const classProperty = config?.className ? `class="${config.className}"` : '';
  const eventId = config?.eventId || `clickable-${new Date().getTime()}`;

  // create event listener and dispatcher
  const onClick = `event.preventDefault();window.dispatchEvent(new Event('${eventId}'))`;
  const removeClickEventListener = createListener(window, eventId, () => config.callback());

  // make element html
  const html = config.href
    ? `<a ${classProperty} href="${config.href}" onclick="${onClick}">${config.text}</a>`
    : `<span ${classProperty} onclick="${onClick}">${config.text}</span>`;

  return {
    html,
    removeClickEventListener,
  };
}
